/**
 * Client-side email validation for the signup surfaces.
 *
 * This is a UX layer, not the authority. functions/src/email_utils.ts is the
 * authoritative validator and additionally checks the TLD against the full IANA
 * list. That list is deliberately NOT duplicated here: it is ~14KB, and a client
 * copy that drifts out of date would reject addresses the server accepts, which
 * is a worse failure than one extra round trip.
 *
 * Two layers live here:
 *   1. Syntax, which blocks. Same pattern as the server.
 *   2. Typo suggestion, which NEVER blocks. See suggestEmailCorrection.
 */

// Mirrors EMAIL_SYNTAX_PATTERN in functions/src/email_utils.ts. Keep in sync.
// "/" is excluded from the local part on purpose: these addresses become
// Firestore document IDs and "a/b/c@x.co" would write into a nested collection.
const EMAIL_SYNTAX_PATTERN =
  /^[A-Za-z0-9!#$%&'*+=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,63}$/;

const MAX_EMAIL_LENGTH = 254;
const MAX_LOCAL_LENGTH = 64;

/**
 * Domains common enough that a near-miss is almost certainly a typo.
 *
 * Membership is load-bearing in both directions. An exact match short-circuits
 * the suggestion, so real providers that sit within one edit of a bigger one
 * (mail.com is one edit from gmail.com, q.com is one from qq.com) MUST be listed
 * here or their users get nagged on every valid address.
 */
export const POPULAR_EMAIL_DOMAINS = [
  "gmail.com", "googlemail.com", "yahoo.com", "yahoo.co.uk", "yahoo.co.jp", "yahoo.fr",
  "yahoo.de", "yahoo.ca", "yahoo.com.br", "yahoo.com.hk", "yahoo.es", "ymail.com",
  "hotmail.com", "hotmail.co.uk", "hotmail.fr", "hotmail.it", "hotmail.es", "hotmail.de",
  "hotmail.ca", "outlook.com", "outlook.fr", "outlook.de", "outlook.es", "live.com",
  "live.co.uk", "live.fr", "live.nl", "live.cn", "live.it", "live.de", "msn.com",
  "aol.com", "icloud.com", "me.com", "mac.com", "protonmail.com", "proton.me", "pm.me",
  "gmx.com", "gmx.de", "gmx.net", "gmx.at", "mail.com", "mail.ru", "mail.de",
  "yandex.ru", "zoho.com", "fastmail.com", "fastmail.fm", "hey.com", "q.com", "duck.com",
  "qq.com", "vip.qq.com", "foxmail.com", "163.com", "126.com", "139.com", "sina.com",
  "sina.cn", "sohu.com", "aliyun.com", "yeah.net", "naver.com", "daum.net", "hanmail.net",
  "web.de", "t-online.de", "freenet.de", "orange.fr", "wanadoo.fr", "free.fr",
  "laposte.net", "sfr.fr", "libero.it", "virgilio.it", "tiscali.it", "telenet.be",
  "skynet.be", "ziggo.nl", "home.nl", "kpnmail.nl", "telia.com", "bluewin.ch",
  "seznam.cz", "centrum.cz", "wp.pl", "o2.pl", "interia.pl", "onet.pl", "bk.ru",
  "terra.com.br", "uol.com.br", "bol.com.br", "comcast.net", "verizon.net", "att.net",
  "sbcglobal.net", "bellsouth.net", "cox.net", "charter.net", "earthlink.net",
  "btinternet.com", "sky.com", "virginmedia.com", "ntlworld.com", "googlemail.co.uk",
  "rediffmail.com", "telus.net", "shaw.ca", "rogers.com", "sympatico.ca",
  "bigpond.com", "optusnet.com.au", "xtra.co.nz",
];

const POPULAR_DOMAIN_SET = new Set(POPULAR_EMAIL_DOMAINS);

/** Trim and lowercase. Does not validate. */
export function normalizeEmail(raw) {
  return typeof raw === 'string' ? raw.trim().toLowerCase() : '';
}

/**
 * Syntax check. Returns { valid, reason, normalized }.
 * reason is one of: 'empty' | 'too_long' | 'syntax'
 */
export function validateEmailSyntax(raw) {
  const normalized = normalizeEmail(raw);

  if (!normalized) {
    return { valid: false, reason: 'empty', normalized };
  }
  if (normalized.length > MAX_EMAIL_LENGTH) {
    return { valid: false, reason: 'too_long', normalized };
  }
  if (!EMAIL_SYNTAX_PATTERN.test(normalized)) {
    return { valid: false, reason: 'syntax', normalized };
  }
  if (normalized.slice(0, normalized.lastIndexOf('@')).length > MAX_LOCAL_LENGTH) {
    return { valid: false, reason: 'too_long', normalized };
  }

  return { valid: true, reason: null, normalized };
}

function levenshtein(a, b) {
  if (Math.abs(a.length - b.length) > 3) return 99;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    for (let j = 1; j <= b.length; j++) {
      cur[j] = Math.min(
        prev[j] + 1,
        cur[j - 1] + 1,
        prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
    }
    prev = cur;
  }
  return prev[b.length];
}

/**
 * Suggest a corrected address when the domain looks like a typo of a popular one.
 * Returns { suggestion, suggestedDomain } or null.
 *
 * This must only ever produce a hint. It must NEVER gate submission, because the
 * tail of legitimate domains sitting within edit distance of a popular one is
 * unbounded, and no exact-match guard can cover all of them. The card renders
 * this as a dismissible "Did you mean ...?" and leaves the button enabled.
 */
export function suggestEmailCorrection(raw) {
  const normalized = normalizeEmail(raw);
  const atIndex = normalized.lastIndexOf('@');
  if (atIndex < 1) return null;

  const local = normalized.slice(0, atIndex);
  const domain = normalized.slice(atIndex + 1);
  if (!domain || POPULAR_DOMAIN_SET.has(domain)) return null;

  // Short domains match too eagerly at distance 2, so scale the threshold.
  const threshold = domain.length <= 6 ? 1 : 2;

  let best = null;
  let bestDistance = Infinity;
  for (const candidate of POPULAR_EMAIL_DOMAINS) {
    const distance = levenshtein(domain, candidate);
    if (distance > 0 && distance <= threshold && distance < bestDistance) {
      bestDistance = distance;
      best = candidate;
    }
  }

  if (!best) return null;
  return { suggestion: `${local}@${best}`, suggestedDomain: best };
}
